import { AbstractViewModel } from '../../../utils/viewmodels/viewmodel';
import { useViewModel } from '../../../utils/viewmodels/useViewModel';
import type { ChatActionsContent, ChatActionItem, ChatActionEventPayload } from '../../chatbot.types';
import type { ChatbotEventBridge } from '../../chatbot-event-bridge';
import type { ChatActionsState } from './chat-actions.state';

export class ChatActionsViewModel extends AbstractViewModel<ChatActionsState> {
  static initialState: ChatActionsState = {
    text: undefined,
    actions: [],
    isGlobalDisabled: false,
    selectedActionId: undefined,
  };

  private readonly bridge: ChatbotEventBridge | null;

  constructor(content: ChatActionsContent, bridge: ChatbotEventBridge | null) {
    super(ChatActionsViewModel.initialState);
    this.bridge = bridge;
    this.setContent(content);
  }

  setContent = (content: ChatActionsContent): void => {
    this.update((state) => {
      state.text = content.text;
      state.actions = content.actions;
      state.isGlobalDisabled = !!content.disabled;
      state.selectedActionId = content.selectedActionId;
    });
  };

  isDisabled = (action: ChatActionItem): boolean => this.state.getValue().isGlobalDisabled || !!action.disabled;

  isSelected = (action: ChatActionItem): boolean => {
    const { selectedActionId } = this.state.getValue();
    return !!selectedActionId && selectedActionId === action.id;
  };

  getLabel = (action: ChatActionItem): string => {
    const disabled = this.isDisabled(action);
    return disabled && action.disabledLabel ? action.disabledLabel : action.label;
  };

  getButtonClassName = (action: ChatActionItem): string => {
    const classes = ['chatbot-action-btn'];
    if (action.primaryButton) classes.push('chatbot-action-btn--primary');
    if (this.isSelected(action)) classes.push('chatbot-action-btn--selected');
    return classes.join(' ');
  };

  handleClick = (action: ChatActionItem): void => {
    const payload: ChatActionEventPayload = {
      payload: action.payload,
      actionId: action.id,
      actionLabel: action.label,
    };
    this.bridge?.emit(action.event, payload);
  };
}

export const useChatActionsViewModel = (content: ChatActionsContent, bridge: ChatbotEventBridge | null) =>
  useViewModel<ChatActionsState, ChatActionsViewModel>(() => new ChatActionsViewModel(content, bridge));
